import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, getUserRole, getUser, clearAuth } from '../utils/auth';
import BottomNav from './BottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const authenticated = isAuthenticated();
    const role = getUserRole();
    const user = getUser();
    const location = useLocation();
    const isAuthPage = ['/user/login', '/admin/login', '/owner/login'].includes(location.pathname);
    const isOwnerPage = location.pathname.startsWith('/owner') && !location.pathname.includes('/login');
    const isAdminPage = location.pathname.startsWith('/admin') && !location.pathname.includes('/login');
    const isEmbeddedView = new URLSearchParams(location.search).get('preview') === '1';
    const isInIframe = window.parent !== window;
    const isIframePreview = isEmbeddedView && isInIframe;
    const isPreviewHomeRoute = location.pathname === '/' || /^\/owner\/?$/.test(location.pathname);

    // Hamburger menu state
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const shouldLockScroll = isDesktop && !isAuthPage && !isEmbeddedView && !isAdminPage;
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        if (shouldLockScroll) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, [isDesktop, isAuthPage, isEmbeddedView]);

    useEffect(() => {
        const targets = [document.documentElement, document.body];
        const previewZoomClass = isPreviewHomeRoute ? 'preview-zoom-home' : 'preview-zoom-out';

        if (isIframePreview) {
            targets.forEach((element) => element.classList.add('preview-scrollbar-hide'));
            document.body.classList.remove('preview-zoom-home', 'preview-zoom-out');
            document.body.classList.add(previewZoomClass);
        } else {
            targets.forEach((element) => element.classList.remove('preview-scrollbar-hide'));
            document.body.classList.remove('preview-zoom-home', 'preview-zoom-out');
        }

        return () => {
            targets.forEach((element) => element.classList.remove('preview-scrollbar-hide'));
            document.body.classList.remove('preview-zoom-home', 'preview-zoom-out');
        };
    }, [isIframePreview, isPreviewHomeRoute]);

    const handleLogout = () => {
        clearAuth();
        toast.success('Logged out successfully');
        navigate('/');
        window.location.reload(); // Ensure state updates cleanly
    };

    const shouldUseDesktopSplit = isDesktop && !isAuthPage && !isEmbeddedView && !isAdminPage;

    useEffect(() => {
        if (!isInIframe || isEmbeddedView) {
            return;
        }

        const previewParams = new URLSearchParams(location.search);
        previewParams.set('preview', '1');
        const previewRoute = `${location.pathname}?${previewParams.toString()}${location.hash || ''}`;
        navigate(previewRoute, { replace: true });
    }, [isInIframe, isEmbeddedView, location.pathname, location.search, location.hash, navigate]);

    useEffect(() => {
        if (!isInIframe) {
            return;
        }

        const previewParams = new URLSearchParams(location.search);
        previewParams.delete('preview');
        const cleanSearch = previewParams.toString();
        const routeForParent = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${location.hash || ''}`;

        window.parent.postMessage(
            {
                type: 'turf-preview-route-change',
                route: routeForParent
            },
            window.location.origin
        );
    }, [isEmbeddedView, location.pathname, location.search, location.hash]);

    useEffect(() => {
        if (!isInIframe) {
            return;
        }

        const syncRoute = () => {
            const previewParams = new URLSearchParams(window.location.search);
            previewParams.delete('preview');
            const cleanSearch = previewParams.toString();
            const routeForParent = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash || ''}`;

            window.parent.postMessage(
                {
                    type: 'turf-preview-route-change',
                    route: routeForParent
                },
                window.location.origin
            );
        };

        window.addEventListener('popstate', syncRoute);
        window.addEventListener('hashchange', syncRoute);

        return () => {
            window.removeEventListener('popstate', syncRoute);
            window.removeEventListener('hashchange', syncRoute);
        };
    }, [isInIframe]);

    useEffect(() => {
        if (!shouldUseDesktopSplit) {
            return;
        }

        const onPreviewRouteChange = (event) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            if (event.data?.type !== 'turf-preview-route-change' || typeof event.data?.route !== 'string') {
                return;
            }

            const currentRoute = `${location.pathname}${location.search || ''}${location.hash || ''}`;
            if (event.data.route !== currentRoute) {
                navigate(event.data.route);
            }
        };

        window.addEventListener('message', onPreviewRouteChange);
        return () => window.removeEventListener('message', onPreviewRouteChange);
    }, [shouldUseDesktopSplit, location.pathname, location.search, location.hash, navigate]);

    if (shouldUseDesktopSplit) {
        const previewParams = new URLSearchParams(location.search);
        previewParams.set('preview', '1');
        const previewSrc = `${location.pathname}?${previewParams.toString()}${location.hash || ''}`;

        return (
            <div className="w-full flex bg-blue-600" style={{ height: '100dvh' }}>
                <section className="w-1/4 bg-[#3f3f44] px-2 py-3 flex items-start justify-center rounded-[16px] overflow-hidden" style={{ height: '100dvh' }}>
                    <div className="w-full max-w-[400px] rounded-[16px] overflow-hidden p-2" style={{ height: 'calc(100dvh - 24px)' }}>
                        <div className="w-full h-full rounded-[12px] overflow-hidden bg-black">
                            <iframe
                                title="Mobile preview"
                                src={previewSrc}
                                className="w-full h-full border-0 rounded-[12px]"
                            //  scrolling="no"
                            />
                        </div>
                    </div>
                </section>

                <section className="w-3/4 h-full bg-blue-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black/95 flex flex-col">
            {/* Desktop/Tablet Header - Sticky - Hidden on Auth Pages and Owner Dashboard */}
            {(!isAuthPage && !isOwnerPage) && (
                <header className="sticky top-0 z-40 bg-gradient-to-b from-black/70 to-black/40 backdrop-blur-xl">
                    <div className="container mx-auto px-4 py-2 flex justify-between items-center">
                        {/* Logo - Left Side */}
                        <Link to="/" className="p-0 m-0">
                            <img
                                src="/Images/Logopb.webp"
                                alt="Turf Booking Logo"
                                width="120"
                                height="60"
                                className="h-10 w-auto md:h-20 md:w-auto object-contain"
                                onError={(e) => {
                                    // Fallback to PNG if WebP fails to load
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.src = '/Images/Logopb.png';
                                }}
                            />
                        </Link>

                        {/* Right Side - Hamburger (Mobile) + Desktop Navigation */}
                        <div className="flex items-center gap-4">
                            {/* Hamburger Menu - Mobile Only (Right Side) - Always show, but hide current role's login */}
                            <div className="relative md:hidden">
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="text-white hover:text-green-400 transition-colors p-2"
                                >
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>

                                {/* Dropdown Menu */}
                                <AnimatePresence>
                                    {isMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute right-0 mt-2 w-60 bg-black/80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 overflow-hidden z-50"
                                        >
                                            <div className="py-2">
                                                {/* User Login - Hide if user is logged in as user */}
                                                {role !== 'user' && (
                                                    <button
                                                        onClick={() => {
                                                            navigate('/user/login');
                                                            setIsMenuOpen(false);
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3"
                                                    >
                                                        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-white text-sm">User Login</p>
                                                            <p className="text-xs text-gray-400">Book turfs & play</p>
                                                        </div>
                                                    </button>
                                                )}

                                                {/* Owner Login - Hide if user is logged in as owner */}
                                                {role !== 'owner' && (
                                                    <button
                                                        onClick={() => {
                                                            navigate('/owner/login');
                                                            setIsMenuOpen(false);
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3"
                                                    >
                                                        <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-white text-sm">Owner Login</p>
                                                            <p className="text-xs text-gray-400">Manage your turfs</p>
                                                        </div>
                                                    </button>
                                                )}

                                                {/* Admin Login - Hide if user is logged in as admin */}
                                                {role !== 'admin' && (
                                                    <button
                                                        onClick={() => {
                                                            navigate('/admin/login');
                                                            setIsMenuOpen(false);
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-3"
                                                    >
                                                        <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-white text-sm">Admin Login</p>
                                                            <p className="text-xs text-gray-400">System administration</p>
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Desktop Navigation */}
                            <div className="hidden md:flex items-center gap-4">
                                <Link to="/search" className="text-gray-600 hover:text-primary-600 font-medium transition-colors">Find Turf</Link>

                                {authenticated ? (
                                    <div className="flex items-center gap-3">
                                        {role === 'admin' && (
                                            <Link to="/admin" className="btn-primary-outline text-sm">Admin Panel</Link>
                                        )}
                                        {role === 'owner' && (
                                            <Link to="/owner" className="btn-primary-outline text-sm">Owner Panel</Link>
                                        )}
                                        <Link to="/user/bookings" className="text-gray-600 hover:text-primary-600 font-medium transition-colors">My Bookings</Link>
                                        <button
                                            onClick={handleLogout}
                                            className="text-gray-600 hover:text-red-500 font-medium transition-colors text-sm"
                                        >
                                            Logout
                                        </button>
                                        <div className="flex items-center gap-2 bg-primary-50 px-3 py-2 rounded-lg border border-primary-200">
                                            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                {user?.name ? user.name[0].toUpperCase() : 'U'}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-800">{user?.name || 'User'}</span>
                                                <span className="text-xs text-gray-600">{user?.mobile ? `+91 ${user.mobile}` : ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <Link
                                        to="/user/login"
                                        className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors shadow-sm"
                                    >
                                        Login
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
            )}

            {/* Main Content */}
            <main className={`flex-1 w-full ${!isAuthPage ? 'pb-20 md:pb-8' : ''}`}>
                <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                >
                    {children}
                </motion.div>
            </main>

            {/* Mobile Bottom Nav */}
            {(!isAuthPage && !isOwnerPage) && <BottomNav />}
        </div>
    );
};

export default Layout;
